import { skipToken } from '@apollo/client/react'
import { useEffect, useRef, useState } from 'react'
import { withQuery } from 'ufo'
import type {
  ArtworkDetailFragment,
  Media,
  TackPreviewFragment,
  Task,
} from 'src/services/apollo/generated/graphql'
import { isAnimatedArtwork } from 'src/utils/artwork'
import { canvasToFile } from 'src/utils/imageUtil'
import { getVideoMediaIdFromTask } from 'src/utils/media'
import { isAnimateDiffTask } from 'src/utils/task'
import { useGetArtwork } from '~/services/apollo/artwork'
import { useMyInfo } from '~/services/apollo/auth'
import { useQs } from '~/utils/qs'

export async function loadImage(
  url: string,
  elem: HTMLImageElement = new Image(),
) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    elem.onload = () => resolve(elem)
    elem.onerror = reject
    elem.src = url
  })
}

export async function fetchToBlob(src: string) {
  const response = await fetch(src, {
    method: 'GET',
  })
  return response.blob()
}

export async function fetchToBlobUrl(src: string) {
  const blob = await fetchToBlob(src)
  return URL.createObjectURL(blob)
}

export async function fetchToBase64(src: string) {
  const response = await fetch(src)
  const blob = await response.blob()
  return new Promise<string | null>((onSuccess, onError) => {
    try {
      const reader = new FileReader()
      reader.onload = function () {
        onSuccess(this.result as string | null)
      }
      reader.readAsDataURL(blob)
    } catch (e) {
      onError(e)
    }
  })
}

export async function downloadFile(
  src: string,
  filename: string,
  options?: {
    /**
     * Skip the WebP-PNG conversion
     */
    skipConversion?: boolean
  },
) {
  const response = await fetch(src, {
    method: 'GET',
  })
  const blob = await response.blob()

  let parsedFilename = filename
  let url = ''
  if (!options?.skipConversion && filename.endsWith('.webp')) {
    parsedFilename = filename.replace('.webp', '.png')
    const file = await convertBlobToPng(blob, parsedFilename)
    if (!file) throw new Error('conversion error: file is invalid')
    url = URL.createObjectURL(file)
  } else {
    // Create blob link to download
    url = URL.createObjectURL(new Blob([blob]))
  }

  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `${parsedFilename}`)
  // Append to html link element page
  document.body.appendChild(link)
  // Start download
  link.click()
  // Clean up and remove the link
  link.parentNode?.removeChild(link)
}

export async function convertBlobToPng(blob: Blob, filename: string) {
  const elem = new Image()
  const loadImage = () =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      elem.onload = () => resolve(elem)
      elem.onerror = reject
      elem.src = URL.createObjectURL(blob)
    })

  try {
    const rawImage = await loadImage()
    let canvas: HTMLCanvasElement | OffscreenCanvas

    if (typeof window.OffscreenCanvas === 'function') {
      canvas = new OffscreenCanvas(rawImage.width, rawImage.height)
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('ctx not initialized')
      ctx.drawImage(rawImage, 0, 0, rawImage.width, rawImage.height)
    } else {
      canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = rawImage.width
      canvas.height = rawImage.height
      if (!ctx) throw new Error('ctx not initialized')
      ctx.drawImage(rawImage, 0, 0, rawImage.width, rawImage.height)
    }

    return canvasToFile(canvas, filename)
  } catch (e) {
    console.log('cannot download image: ', e)
  }
}

const ALT_MAX_LENGTH = 160

export function getArtworkAlt(
  artwork?: Pick<ArtworkDetailFragment, 'title' | 'prompts' | 'hidePrompts'> & {
    tacks?: TackPreviewFragment[] | null
  },
  modelTitle?: string,
): string {
  if (!artwork) return 'AI-generated artwork on PixAI'
  const title = artwork.title?.trim()
  const titlePart = title ? `AI art "${title}"` : 'AI artwork'
  const tackNames =
    artwork.tacks
      ?.map(t => (t.displayName ?? t.defaultName).trim())
      .filter(Boolean)
      .map(name => `#${name}`) ?? []
  const tagsPart =
    tackNames.length > 0 ? `featuring ${naturalJoin(tackNames)}` : ''
  const byModelPart = modelTitle ? `generated with ${modelTitle}` : ''
  const promptsPart =
    artwork.prompts && !artwork.hidePrompts
      ? `for keywords ${artwork.prompts}`
      : ''
  // Progressive truncation: drop least important parts first
  // Priority: title > tags > model > prompts
  const tiers = [
    [titlePart, tagsPart, byModelPart, promptsPart],
    [titlePart, tagsPart, byModelPart],
    [titlePart, byModelPart],
    [titlePart],
  ]
  for (const parts of tiers) {
    const result = parts.filter(Boolean).join(' ')
    if (result.length <= ALT_MAX_LENGTH) return result
  }
  return titlePart.slice(0, ALT_MAX_LENGTH)
}

function naturalJoin(input: string[]) {
  const last = input.pop()
  if (input.length === 0) {
    return last
  }

  return `${input.join(', ')} and ${last}`
}

export function getImageDownloadFilename({
  artwork,
  task,
  mediaIdx,
}: {
  artwork?: Pick<ArtworkDetailFragment, 'id' | 'title'>
  task?: Pick<Task, 'id'>
  mediaIdx?: number
}) {
  const prefix = 'from-PixAI'
  if (artwork) {
    return `${prefix}-${artwork.id}${artwork.title ? '-' + artwork.title : ''}`
  }

  if (task) {
    if (mediaIdx != null) {
      return `${prefix}-${task.id}-${mediaIdx}`
    }
    return `${prefix}-${task.id}`
  }
  return `${prefix}-image`
}

/**
 * Task is needed to determine whether media has a corresponding video or animated webp
 * Some media doesn't have a matching task (e.g. User uploaded artworks)
 * If media or { imageType: string } is provided, task.media.imageType will be ignored
 * @param arg - `{ task, media }`
 * For arg.media: only `media.imageType` is actually used
 * @returns
 */
export function getImageDownloadFileExt({
  task,
  artwork,
  media,
}: {
  task?: Pick<Task, 'parameters' | 'outputs' | 'media'>
  artwork?: Pick<ArtworkDetailFragment, 'videoMediaId' | 'type'>
  media?: Partial<Media>
}) {
  if (getVideoMediaIdFromTask(task) || artwork?.videoMediaId) return 'mp4'
  if (isAnimateDiffTask(task) || isAnimatedArtwork(artwork)) return 'webp'

  // * Notice
  // The backend side have some confusing logic about imageType
  // The comments on this filed on backend side and what actually stored in the database
  // are not always the same, so we need to handle both cases
  // It might be mimeType with image/ prefix or just the file extension name
  // Here we try to handle both cases
  const imageType = (media?.imageType || task?.media?.imageType) ?? 'png'
  if (imageType.startsWith('image/')) {
    const split = imageType.split('/')
    if (split.length > 1) {
      return split[1].toLowerCase()
    }
  }
  return imageType
}

export function getOpenArtworkDetailQuery(
  currentUrl: string,
  artworkId: string,
) {
  return withQuery(currentUrl, {
    artworkId,
    openDetail: 'true',
  })
}

/**
 * Consume the url query generated by `getOpenArtworkDetailQuery`
 *
 * @see getOpenArtworkDetailQuery
 */
export default function useArtworkIdFromQueryParams(args: {
  onArtworkOpen: (artwork: ArtworkDetailFragment, newWindow?: boolean) => void
}) {
  const { data: userInfo } = useMyInfo()
  const [{ artworkId, openDetail }, setQs] = useQs()
  const ensureArtworkIdOnceRef = useRef(false)
  const ensureArtworkOpenOnceRef = useRef(false)
  const [artworkIdToUse, setArtworkIdToUse] = useState<string>()

  // NOTE: ArtworkGrid will reload after userInfo changes
  // Have to wait before userInfo is loaded
  useEffect(() => {
    if (!userInfo) return
    if (!artworkId || !openDetail) return
    // Ensure Set once
    if (ensureArtworkIdOnceRef.current) return
    if (typeof artworkId === 'string' && openDetail === 'true') {
      setArtworkIdToUse(artworkId)
    }
    ensureArtworkIdOnceRef.current = true
    setQs(prev => ({ ...prev, artworkId: null, openDetail: null }))
  }, [userInfo?.id, artworkId, openDetail])

  const { data: artwork } = useGetArtwork(artworkIdToUse ?? skipToken)

  // Wait for artwork to be loaded
  useEffect(() => {
    if (ensureArtworkOpenOnceRef.current) return

    if (artwork?.id) {
      // Ensure open once
      ensureArtworkOpenOnceRef.current = true
      args.onArtworkOpen(artwork)
    }
  }, [artwork?.id])

  return artworkIdToUse
}
